import React, { useMemo, useState, useEffect } from "react";
import { Table, Form, Row, Col, Dropdown, Badge } from "react-bootstrap";
import { Modal , ModalHeader} from "../../../../globalComponents/ui/Modal";

/* ---------------- helpers ---------------- */
const formatDate = (date) => {
  if (!date) return "—";
  const d = new Date(date);
  return isNaN(d.getTime()) ? "—" : d.toLocaleDateString("en-GB");
};

const normalizeSourceIds = (sourceIds) => {
  if (!sourceIds) return [];
  if (Array.isArray(sourceIds)) return sourceIds.map(String);
  try {
    const parsed = typeof sourceIds === "string" ? JSON.parse(sourceIds) : sourceIds;
    return Array.isArray(parsed) ? parsed.map(String) : [];
  } catch { return []; }
};

/* ---------------- component ---------------- */
const AssignModal = ({ show, onHide, item, sources = [] }) => {
  const assignments = item?.assign_raw_list || [];

  /* sourceId → display name */
  const sourceMap = useMemo(() => {
    const map = new Map();
    sources.forEach(s => {
      if (s?.id) map.set(String(s.id), s.unitCode || s.name || `ID ${s.id}`);
    });
    return map;
  }, [sources]);

  /*
   * FIX: locationOptions now only includes locations that actually appear
   * in this question's assignment records — not every source in the system.
   * Previously it used Array.from(sourceMap.values()) which showed all sources.
   */
  const locationOptions = useMemo(() => {
    const names = new Set();
    assignments.forEach(a => {
      (a.assignedToDetails || []).forEach(u => {
        normalizeSourceIds(u.source_ids).forEach(id => {
          const name = sourceMap.get(id);
          if (name) names.add(name);
        });
      });
    });
    return Array.from(names).sort();
  }, [assignments, sourceMap]);

  const [filters, setFilters] = useState({
    locations: [],
    assignedBy: "",
    assignedTo: "",
  });

  /* Default: all locations selected */
  useEffect(() => {
    setFilters(prev => ({ ...prev, locations: [...locationOptions] }));
  }, [locationOptions.join(",")]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleLocationToggle = (loc) => {
    setFilters(prev => {
      const isSelected = prev.locations.includes(loc);
      const newLocs = isSelected
        ? prev.locations.filter(l => l !== loc)
        : [...prev.locations, loc];
      return { ...prev, locations: newLocs };
    });
  };

  const handleSelectAll = () => {
    setFilters(prev => ({
      ...prev,
      locations: prev.locations.length === locationOptions.length ? [] : [...locationOptions]
    }));
  };

  const rows = useMemo(() => {
    const results = [];
    assignments.forEach(a => {
      (a.assignedToDetails || []).forEach(u => {
        const sourceIds = normalizeSourceIds(u.source_ids);
        const unitCodes = sourceIds.map(id => sourceMap.get(id)).filter(Boolean);
        const assignedBy = (a.assignedByDetails || [])
          .map(b => `${b.first_name || ""} ${b.last_name || ""}`.trim())
          .filter(Boolean)
          .join(", ") || "System";
        const assignedTo = `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email || "—";

        /*
         * Location filter:
         *  - if no locationOptions exist (no source_ids), always show the row
         *  - otherwise at least one of the user's locations must be selected
         */
        const matchesLocation =
          locationOptions.length === 0 ||
          filters.locations.length === 0 ||
          unitCodes.some(c => filters.locations.includes(c));

        const matchesBy = !filters.assignedBy ||
          assignedBy.toLowerCase().includes(filters.assignedBy.toLowerCase());

        const matchesTo = !filters.assignedTo ||
          assignedTo.toLowerCase().includes(filters.assignedTo.toLowerCase());

        if (matchesLocation && matchesBy && matchesTo) {
          results.push({
            assignedBy,
            assignedTo,
            createdAt: a.createdAt,
            dueDate: a.dueDate,
            locations: unitCodes.join(", ") || "—",
          });
        }
      });
    });
    return results;
  }, [assignments, filters, sourceMap, locationOptions]);

  const getDropdownLabel = () => {
    if (locationOptions.length === 0)                            return "No Locations";
    if (filters.locations.length === 0)                          return "None Selected";
    if (filters.locations.length === locationOptions.length)     return "All Locations Selected";
    if (filters.locations.length > 2)                            return `${filters.locations.length} Locations Selected`;
    return filters.locations.join(", ");
  };

  const itemStyle = {
    padding: "8px 16px",
    background: "transparent",
    color: "inherit",
    cursor: "pointer"
  };

  return (
    <Modal show={show} onClose={onHide} maxWidth={940}>
      <ModalHeader emoji="👤" title="Assignment History" subtitle={item?.kpi_name} onClose={onHide} />

      {/* ── Filter bar ── */}
      <div style={{ padding: "16px 20px", background: "#fdfdfd", borderBottom: "1px solid #eee" }}>
        <Row className="align-items-end g-3">

          {/* Location multi-select — only shown when there are locations in the assignments */}
          {locationOptions.length > 0 && (
            <Col md={4}>
              <Form.Label className="small fw-bold text-secondary mb-1">Locations</Form.Label>
              <Dropdown autoClose="outside" className="w-100 custom-dropdown">
                <Dropdown.Toggle
                  variant="outline-secondary"
                  className="w-100 d-flex justify-content-between align-items-center bg-white shadow-none"
                  style={{ fontSize: "13px", textAlign: "left", borderColor: "#ced4da", color: "#444" }}
                >
                  <span className="text-truncate" style={{ maxWidth: "90%" }}>{getDropdownLabel()}</span>
                </Dropdown.Toggle>

                <Dropdown.Menu className="w-100 shadow-sm border-0 mt-1" style={{ maxHeight: "250px", overflowY: "auto", border: "1px solid #ddd" }}>
                  <div onClick={handleSelectAll} style={{ ...itemStyle, borderBottom: "1px solid #f0f0f0", fontWeight: "bold" }}>
                    <Form.Check
                      type="checkbox" id="select-all" label="Select All"
                      checked={filters.locations.length === locationOptions.length}
                      onChange={() => {}} style={{ pointerEvents: "none" }}
                    />
                  </div>
                  {locationOptions.map(l => (
                    <div key={l} onClick={() => handleLocationToggle(l)} className="dropdown-item-no-hover" style={itemStyle}>
                      <Form.Check
                        type="checkbox" id={`loc-${l}`} label={l}
                        checked={filters.locations.includes(l)}
                        onChange={() => {}} style={{ pointerEvents: "none" }}
                      />
                    </div>
                  ))}
                </Dropdown.Menu>
              </Dropdown>
            </Col>
          )}

          {/* Assigned By search */}
          <Col md={locationOptions.length > 0 ? 4 : 6}>
            <Form.Label className="small fw-bold text-secondary mb-1">Assigned By</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Search assigned by…"
              value={filters.assignedBy}
              onChange={e => setFilters(p => ({ ...p, assignedBy: e.target.value }))}
            />
          </Col>

          {/* Assigned To search */}
          <Col md={locationOptions.length > 0 ? 4 : 6}>
            <Form.Label className="small fw-bold text-secondary mb-1">Assigned To</Form.Label>
            <Form.Control
              size="sm"
              placeholder="Search assigned to…"
              value={filters.assignedTo}
              onChange={e => setFilters(p => ({ ...p, assignedTo: e.target.value }))}
            />
          </Col>
        </Row>
      </div>

      {/* ── Table ── */}
      <div style={{ padding: "0 20px 20px 20px", maxHeight: "60vh", overflowY: "auto" }}>
        {assignments.length === 0 ? (
          <div style={{ textAlign: "center", padding: "52px 24px", color: "#94a3b8" }}>
            <div style={{ fontSize: 38, marginBottom: 12 }}>📋</div>
            <div style={{ fontWeight: 700, fontSize: 14, color: "#64748b", marginBottom: 6 }}>No assignments yet</div>
            <div style={{ fontSize: 13 }}>This KPI has not been assigned to anyone.</div>
          </div>
        ) : (
          <Table hover responsive className="mt-3 align-middle" style={{ fontSize: 13, border: "1px solid #eee" }}>
            <thead style={{ position: "sticky", top: 0, background: "#fff", zIndex: 1 }}>
              <tr className="table-light">
                <th className="py-3 border-bottom">Assigned By</th>
                <th className="py-3 border-bottom">Assigned To</th>
                <th className="py-3 border-bottom text-nowrap">Assigned Date</th>
                <th className="py-3 border-bottom">Location</th>
              </tr>
            </thead>
            <tbody>
              {rows.length > 0 ? (
                rows.map((r, i) => (
                  <tr key={i}>
                    <td className="fw-medium text-dark">{r.assignedBy}</td>
                    <td className="fw-medium">{r.assignedTo}</td>
                    <td className="text-muted">{formatDate(r.createdAt)}</td>
                    <td><small className="text-muted">{r.locations}</small></td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={4} className="text-center py-5 text-muted">
                    No records found matching your selection.
                  </td>
                </tr>
              )}
            </tbody>
          </Table>
        )}
      </div>

      <style>{`
        .dropdown-item-no-hover:hover {
          background-color: #f8f9fa !important;
          color: inherit !important;
        }
        .custom-dropdown .dropdown-toggle::after {
          margin-left: 10px;
        }
      `}</style>
    </Modal>
  );
};

export default AssignModal;